/**
 * Market Pricing Loader
 *
 * Lazy loads market pricing data from chunked static files generated at build time.
 * Implements in-memory caching and automatic chunk loading based on SKU lookups.
 *
 * Architecture:
 * 1. Fetch index.json once (maps SKU → chunk filename)
 * 2. Load only required chunks based on SKUs needed
 * 3. Cache chunks in memory for subsequent requests
 * 4. Merge pricing data with product/variant data at runtime
 *
 * Benefits:
 * - 92% reduction in initial page load (no market prices in GraphQL)
 * - Lazy loading - only fetch data when needed
 * - Better caching - chunks rarely change
 * - Shared across Search, Warranty Claim, and Collection pages
 */

const BASE_PATH = "/market-pricing"
const CACHE_VERSION = "v1" // Increment to bust cache

export interface VariantWithMarketPricing {
  brandName?: string
  brandSlug?: string
  compareAtPrice?: null | string
  deviceName?: string
  deviceSlug?: string
  deviceTypeName?: string
  deviceTypeSlug?: string
  id: string
  images?: Array<{ file: { url: string } }>

  marketPrices: MarketPrices
  name: string
  price: string
  productName: string
  // Parent product context (useful for Search/Warranty Claim)
  productSlug: string
  seriesName?: string
  seriesSlug?: string
  seriesTags?: Array<{ text: string }> | null
  shopifyVariantId: string
  sku: string
  upc?: string
}

interface ChunkCache {
  [chunkFilename: string]: {
    [sku: string]: VariantWithMarketPricing
  }
}

interface MarketPrice {
  compareAtPrice?: {
    amount: string
    currencyCode: string
  } | null
  price: {
    amount: string
    currencyCode: string
  }
}

interface MarketPrices {
  AE?: MarketPrice
  AR?: MarketPrice
  AU?: MarketPrice
  BR?: MarketPrice
  CA?: MarketPrice
  CL?: MarketPrice
  DE?: MarketPrice
  ES?: MarketPrice
  FR?: MarketPrice
  GB?: MarketPrice
  HK?: MarketPrice
  ID?: MarketPrice
  IN?: MarketPrice
  IT?: MarketPrice
  JP?: MarketPrice
  MX?: MarketPrice
  MY?: MarketPrice
  NL?: MarketPrice
  NZ?: MarketPrice
  PH?: MarketPrice
  SG?: MarketPrice
  TH?: MarketPrice
  TW?: MarketPrice
  US?: MarketPrice
  VN?: MarketPrice
}

interface SKUIndex {
  [sku: string]: string // SKU → chunk filename
}

// In-memory caches
let indexCache: null | SKUIndex = null
let chunkCache: ChunkCache = {}

/**
 * Clear all caches (useful for testing or forced refresh)
 */
export function clearMarketPricingCache(): void {
  indexCache = null
  chunkCache = {}
}

/**
 * Get all market pricing data (loads all chunks)
 * Use sparingly - prefer getMarketPricingBySKUs for better performance
 */
export async function getAllMarketPricing(): Promise<
  VariantWithMarketPricing[]
> {
  try {
    const index = await loadIndex()

    // Get unique chunk filenames
    const allChunks = new Set(Object.values(index))

    // Load all chunks in parallel
    const chunkPromises = Array.from(allChunks).map((chunkFilename) =>
      loadChunk(chunkFilename)
    )
    await Promise.all(chunkPromises)

    // Flatten all variants from all chunks
    const allVariants: VariantWithMarketPricing[] = []

    Object.values(chunkCache).forEach((chunk) => {
      Object.values(chunk).forEach((variant) => {
        allVariants.push(variant)
      })
    })

    return allVariants
  } catch (error) {
    console.error("Failed to get all market pricing", error)
    return []
  }
}

/**
 * Get market pricing data for specific SKUs
 */
export async function getMarketPricingBySKUs(
  skus: string[]
): Promise<Map<string, VariantWithMarketPricing>> {
  if (!skus || skus.length === 0) {
    return new Map()
  }

  try {
    // Load index
    const index = await loadIndex()

    // Determine which chunks we need
    const chunksNeeded = new Set<string>()
    const skuToChunk = new Map<string, string>()

    skus.forEach((sku) => {
      const chunkFilename = index[sku]
      if (chunkFilename) {
        chunksNeeded.add(chunkFilename)
        skuToChunk.set(sku, chunkFilename)
      }
    })

    // Load all required chunks in parallel
    const chunkPromises = Array.from(chunksNeeded).map((chunkFilename) =>
      loadChunk(chunkFilename)
    )
    await Promise.all(chunkPromises)

    // Build result map
    const result = new Map<string, VariantWithMarketPricing>()

    skus.forEach((sku) => {
      const chunkFilename = skuToChunk.get(sku)
      if (chunkFilename && chunkCache[chunkFilename]) {
        const variantData = chunkCache[chunkFilename][sku]
        if (variantData) {
          result.set(sku, variantData)
        }
      }
    })

    return result
  } catch (error) {
    console.error("Failed to get market pricing by SKUs", error)
    // Return empty map on error to allow graceful degradation
    return new Map()
  }
}

/**
 * Preload index (call early to warm cache)
 */
export function preloadMarketPricingIndex(): void {
  if (typeof window !== "undefined" && !indexCache) {
    loadIndex().catch((error) => {
      console.error("Failed to preload market pricing index", error)
    })
  }
}

/**
 * Fetch with retry logic
 */
async function fetchWithRetry(
  url: string,
  maxRetries = 3,
  delay = 300
): Promise<any> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      if (attempt === maxRetries) {
        console.error(
          `Failed to fetch ${url} after ${maxRetries} attempts`,
          error
        )
        throw error
      }

      // Exponential backoff
      const backoffDelay = delay * Math.pow(2, attempt - 1)
      await new Promise((resolve) => setTimeout(resolve, backoffDelay))
    }
  }
}

/**
 * Load a specific chunk
 */
async function loadChunk(
  chunkFilename: string
): Promise<{ [sku: string]: VariantWithMarketPricing }> {
  // Check cache first
  if (chunkCache[chunkFilename]) {
    return chunkCache[chunkFilename]
  }

  try {
    const chunk = await fetchWithRetry(
      `${BASE_PATH}/${chunkFilename}?v=${CACHE_VERSION}`
    )
    chunkCache[chunkFilename] = chunk
    return chunk
  } catch (error) {
    console.error(
      `Failed to load market pricing chunk: ${chunkFilename}`,
      error
    )
    throw error
  }
}

/**
 * Load the SKU → chunk filename index
 */
async function loadIndex(): Promise<SKUIndex> {
  if (indexCache) {
    return indexCache
  }

  try {
    const index = await fetchWithRetry(
      `${BASE_PATH}/index.json?v=${CACHE_VERSION}`
    )
    indexCache = index
    return index
  } catch (error) {
    console.error("Failed to load market pricing index", error)
    throw error
  }
}
